import React, { useEffect, useState, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

// Image wrapper component that adds styling similar to the home page
const ServiceImage = ({ src, alt, title }) => {
  return (
    <div className="service-image-container animate-fadeUp">
      <div className="service-image-wrapper">
        <img 
          src={src} 
          alt={alt || title} 
          className="service-image" 
        />
        {/* Overlay elements to help blend the image */}
        <div className="service-image-glow"></div>
        <div className="service-image-circle service-image-circle-1"></div>
        <div className="service-image-circle service-image-circle-2"></div>
      </div>
    </div>
  );
};

const Services = () => {
  const { serviceId } = useParams();
  const [activeService, setActiveService] = useState(null);
  
  // Move the servicesData definition into useMemo to prevent recreating it on each render
  // while still allowing ESLint to be happy with dependencies
  const servicesData = useMemo(() => [
    {
      id: 'web-development',
      title: 'Web Development',
      icon: 'fas fa-laptop-code',
      description: 'Custom websites and web applications built with cutting-edge technologies.',
      fullDescription: `
        <p>Our web development services focus on creating high-performance, scalable, and secure websites and web applications that provide exceptional user experiences across all devices.</p>
        
        <p>Whether you need a simple informational website, a complex e-commerce platform, or a custom web application, our team of expert developers has the skills and experience to bring your vision to life.</p>
      `,
      features: [
        'Responsive Design',
        'Custom CMS Development',
        'E-commerce Websites',
        'Progressive Web Apps',
        'Single Page Applications',
        'Web Portal Development',
        'API Development & Integration'
      ],
      technologies: ['React', 'Angular', 'Vue.js', 'Node.js', 'PHP', 'Laravel', 'WordPress', 'MongoDB', 'MySQL'],
      image: '/images/services/web-development.jpg'
    },
    {
      id: 'app-development',
      title: 'Mobile App Development',
      icon: 'fas fa-mobile-alt',
      description: 'Native and cross-platform mobile applications for iOS and Android devices.',
      fullDescription: `
        <p>We create stunning, feature-rich mobile applications that engage users and drive business growth. Our mobile development team builds native apps for iOS and Android, as well as cross-platform solutions that work seamlessly across multiple devices.</p>
        
        <p>From concept to launch, we handle every aspect of mobile app development, ensuring your app stands out in today's competitive marketplace.</p>
      `,
      features: [
        'Native iOS Development',
        'Native Android Development',
        'Cross-Platform Development',
        'UI/UX Design for Mobile',
        'App Store Optimization',
        'App Testing & QA',
        'App Maintenance & Support',
        'Push Notification Integration'
      ],
      technologies: ['Swift', 'Kotlin', 'React Native', 'Flutter', 'Firebase', 'iOS SDK', 'Android SDK'],
      image: '/images/services/app-development.jpg'
    },
    {
      id: 'ui-ux-design',
      title: 'UI/UX Design',
      icon: 'fas fa-palette',
      description: 'User-centered design creating beautiful, intuitive interfaces and exceptional experiences.',
      fullDescription: `
        <p>Great design is at the heart of successful digital products. Our UI/UX design services focus on creating intuitive, engaging, and accessible user interfaces that delight your users and achieve your business goals.</p>
        
        <p>We follow a user-centered design approach, combining research, testing, and iteration to ensure your product not only looks beautiful but also provides an exceptional user experience.</p>
      `,
      features: [
        'User Research & Analysis',
        'Information Architecture',
        'Wireframing & Prototyping',
        'Visual Design',
        'Interaction Design',
        'Usability Testing',
        'Design Systems',
        'Responsive Design'
      ],
      technologies: ['Figma', 'Adobe XD', 'Sketch', 'InVision', 'Principle', 'Zeplin'],
      image: '/images/services/ui-ux-design.jpg'
    },
    {
      id: 'ecommerce',
      title: 'E-Commerce Solutions',
      icon: 'fas fa-shopping-cart',
      description: 'Powerful online stores with seamless checkout and integrated payment systems.',
      fullDescription: `
        <p>Transform your retail business with our comprehensive e-commerce solutions. We build online stores that drive sales, enhance customer experience, and streamline your operations.</p>
        
        <p>From small boutique shops to large enterprise retailers, we create custom e-commerce platforms that are secure, scalable, and optimized for conversions.</p>
      `,
      features: [
        'Custom E-commerce Development',
        'Shopping Cart Integration',
        'Payment Gateway Integration',
        'Inventory Management',
        'Order Processing Systems',
        'Customer Account Management',
        'Product Search & Filtering',
        'Shipping Integration',
        'Analytics & Reporting'
      ],
      technologies: ['Shopify', 'WooCommerce', 'Magento', 'BigCommerce', 'Stripe', 'PayPal', 'Square'],
      image: '/images/services/ecommerce.jpg'
    },
    {
      id: 'custom-software',
      title: 'Custom Software Development',
      icon: 'fas fa-cogs',
      description: 'Tailor-made software solutions that automate processes and solve business challenges.',
      fullDescription: `
        <p>Our custom software development services provide tailored solutions to address your unique business challenges. We develop scalable, secure, and efficient software that automates processes, increases productivity, and drives innovation.</p>
        
        <p>From enterprise applications to specialized tools, we work closely with you to understand your requirements and deliver software that meets your specific needs.</p>
      `,
      features: [
        'Custom Business Applications',
        'Enterprise Resource Planning (ERP)',
        'Customer Relationship Management (CRM)',
        'Content Management Systems',
        'Automated Workflow Systems',
        'Data Processing Applications',
        'Cloud Solutions',
        'Legacy System Integration'
      ],
      technologies: ['Java', 'C#/.NET', 'Python', 'PHP', 'Node.js', 'AWS', 'Azure', 'Docker', 'Kubernetes'],
      image: '/images/services/custom-software.jpg'
    },
    {
      id: 'it-consultancy',
      title: 'IT Consultancy',
      icon: 'fas fa-chalkboard-teacher',
      description: 'Strategic technology consulting for informed digital infrastructure decisions.',
      fullDescription: `
        <p>Navigate the complex world of technology with our expert IT consultancy services. We provide strategic guidance to help you make informed decisions about your digital infrastructure, technology investments, and IT strategies.</p>
        
        <p>Our consultants bring years of experience across various industries and technologies, offering valuable insights to optimize your IT operations and drive business growth.</p>
      `,
      features: [
        'IT Strategy Development',
        'Digital Transformation Consulting',
        'Technology Assessment',
        'Solution Architecture',
        'Vendor Selection',
        'IT Infrastructure Planning',
        'Security Assessment',
        'Cloud Migration Strategy'
      ],
      technologies: [],
      image: '/images/services/it-consultancy.jpg'
    }
  ], []);
  
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    // If a serviceId is provided in the URL, find and set the active service
    if (serviceId) {
      const service = servicesData.find(s => s.id === serviceId);
      if (service) {
        setActiveService(service);
      }
    } else {
      // Reset active service if on main services page
      setActiveService(null);
    }
  }, [serviceId, servicesData]); // Added servicesData to the dependency array
  
  // If a specific service is selected, show its detailed view
  if (activeService) {
    return (
      <>
        {/* Hero Section */}
        <section className="hero" style={{ backgroundColor: 'var(--dark-light)' }}>
          <div className="container">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <Link to="/services" className="btn btn-outline btn-sm">
                <i className="fas fa-arrow-left"></i> All Services
              </Link>
            </div>
            <h1 className="animate-fadeUp">{activeService.title}</h1>
            <p className="hero-description animate-fadeUp delay-1">
              {activeService.description}
            </p>
          </div>
          
          {/* Add background graphics similar to the hero section on homepage */}
          <div className="hero-graphics">
            <div className="hero-circle hero-circle-1"></div>
            <div className="hero-circle hero-circle-2"></div>
          </div>
        </section>
        
        {/* Service Details */}
        <section className="section">
          <div className="container">
            <div className="grid grid-2">
              <div className="animate-fadeUp">
                <div className="mb-4" dangerouslySetInnerHTML={{ __html: activeService.fullDescription }}></div>
                
                <h3 className="mb-3">Key Features</h3>
                <ul style={{ listStyle: 'none', padding: 0, marginBottom: '2rem' }}>
                  {activeService.features.map((feature, index) => (
                    <li key={index} style={{ marginBottom: '0.75rem', display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                      <i className="fas fa-check-circle" style={{ color: 'var(--secondary)', marginTop: '0.25rem' }}></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {activeService.technologies.length > 0 && (
                  <>
                    <h3 className="mb-3">Technologies We Use</h3>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '2rem' }}>
                      {activeService.technologies.map((tech, index) => (
                        <span 
                          key={index} 
                          style={{ 
                            padding: '0.5rem 1rem', 
                            backgroundColor: 'rgba(255, 255, 255, 0.05)', 
                            borderRadius: '2rem',
                            fontSize: '0.875rem'
                          }}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </>
                )}
                
                <Link to="/contact" className="btn">
                  Discuss Your Project
                </Link>
              </div>
              
              <div>
                {/* Replace the standard image with our new ServiceImage component */}
                <ServiceImage 
                  src={activeService.image} 
                  alt={activeService.title} 
                />
                
                <div className="card animate-fadeUp delay-2" style={{ padding: '2rem' }}>
                  <h3 className="mb-4">Ready to get started?</h3>
                  <p className="mb-4">We'll help you choose the right solution for your business needs. Contact us for a free consultation.</p>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <Link to="/contact" className="btn">
                      Contact Us
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Other Services */}
        <section className="section" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
          <div className="container">
            <div className="text-center mb-5">
              <h2 className="animate-fadeUp">Explore Our Other Services</h2>
              <p className="animate-fadeUp delay-1">Discover the full range of digital solutions we offer</p>
            </div>
            
            <div className="services-grid">
              {servicesData
                .filter(service => service.id !== activeService.id)
                .slice(0, 3)
                .map((service, index) => (
                  <div key={service.id} className="service-card animate-fadeUp" style={{ animationDelay: `${0.1 * index}s` }}>
                    <div className="service-icon">
                      <i className={service.icon}></i>
                    </div>
                    <h3 className="service-title">{service.title}</h3>
                    <p className="service-description">
                      {service.description}
                    </p>
                    <Link to={`/services/${service.id}`} className="btn btn-outline">Learn More</Link>
                  </div>
                ))
              }
            </div>
          </div>
        </section>
        
        {/* CTA Section */}
        <section className="cta-section">
          <div className="container cta-content">
            <h2 className="cta-title animate-fadeUp">Ready to Transform Your Business?</h2>
            <p className="cta-description animate-fadeUp delay-1">
              Let's discuss how our {activeService.title} services can help you achieve your goals.
            </p>
            <Link to="/contact" className="btn btn-secondary animate-fadeUp delay-2">
              Get a Free Consultation
            </Link>
          </div>
        </section>
      </>
    );
  }
  
  // Main services page
  return (
    <>
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <h1 className="animate-fadeUp">Our Services</h1>
          <p className="hero-description animate-fadeUp delay-1">
            We offer comprehensive digital solutions to help businesses of all sizes thrive in the digital landscape.
          </p>
        </div>
        
        {/* Add background graphics similar to the hero section on homepage */}
        <div className="hero-graphics">
          <div className="hero-circle hero-circle-1"></div>
          <div className="hero-circle hero-circle-2"></div>
        </div>
      </section>
      
      {/* Services Section */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="animate-fadeUp">What We Offer</h2>
            <p className="animate-fadeUp delay-1">Discover our range of services tailored to meet your business needs</p>
          </div>
          
          <div className="services-grid">
            {servicesData.map((service, index) => (
              <div 
                key={service.id} 
                className="service-card animate-fadeUp"
                style={{ animationDelay: `${0.1 * index}s` }}
              >
                <div className="service-icon">
                  <i className={service.icon}></i>
                </div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-description">
                  {service.description}
                </p>
                <Link to={`/services/${service.id}`} className="btn btn-outline">Learn More</Link>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Process Section */}
      <section className="section" style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="animate-fadeUp">Our Development Process</h2>
            <p className="animate-fadeUp delay-1">We follow a structured approach to deliver high-quality results</p>
          </div>
          
          <div className="process-steps">
            <div className="process-step animate-fadeUp" style={{ animationDelay: '0.1s' }}>
              <div className="process-number">1</div>
              <div className="process-content">
                <h3 className="process-title">Discovery & Planning</h3>
                <p>
                  We start by understanding your business goals, target audience, and project requirements.
                  This helps us create a detailed roadmap for your project.
                </p>
              </div>
            </div>

            <div className="process-step animate-fadeUp" style={{ animationDelay: '0.2s' }}>
              <div className="process-number">2</div>
              <div className="process-content">
                <h3 className="process-title">Design & Prototyping</h3>
                <p>
                  Our designers create wireframes and interactive prototypes to visualize the user interface
                  and experience. We refine these based on your feedback.
                </p>
              </div>
            </div>

            <div className="process-step animate-fadeUp" style={{ animationDelay: '0.3s' }}>
              <div className="process-number">3</div>
              <div className="process-content">
                <h3 className="process-title">Development</h3>
                <p>
                  Our development team brings the designs to life, writing clean, efficient code
                  and implementing all required functionality.
                </p>
              </div>
            </div>

            <div className="process-step animate-fadeUp" style={{ animationDelay: '0.4s' }}>
              <div className="process-number">4</div>
              <div className="process-content">
                <h3 className="process-title">Testing & Quality Assurance</h3>
                <p>
                  We rigorously test all features to ensure everything works perfectly across all devices
                  and browsers, fixing any issues that arise.
                </p>
              </div>
            </div>

            <div className="process-step animate-fadeUp" style={{ animationDelay: '0.5s' }}>
              <div className="process-number">5</div>
              <div className="process-content">
                <h3 className="process-title">Deployment & Launch</h3>
                <p>
                  Once everything is approved, we deploy your project to the live environment,
                  ensuring a smooth launch.
                </p>
              </div>
            </div>

            <div className="process-step animate-fadeUp" style={{ animationDelay: '0.6s' }}>
              <div className="process-number">6</div>
              <div className="process-content">
                <h3 className="process-title">Ongoing Support</h3>
                <p>
                  Our relationship doesn't end at launch. We provide ongoing support and maintenance
                  to ensure your digital product continues to perform at its best.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="section">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="animate-fadeUp">Frequently Asked Questions</h2>
            <p className="animate-fadeUp delay-1">Find answers to common questions about our services</p>
          </div>
          
          <div className="grid grid-2">
            <div className="card animate-fadeUp" style={{ padding: '1.5rem', animationDelay: '0.1s' }}>
              <h3 className="mb-3">How long does it take to complete a project?</h3>
              <p>
                Project timelines vary depending on the scope and complexity. A simple website might take 4-6 weeks, 
                while a complex web application or mobile app could take 3-6 months. During our initial consultation, 
                we'll provide you with a more accurate timeline based on your specific requirements.
              </p>
            </div>
            
            <div className="card animate-fadeUp" style={{ padding: '1.5rem', animationDelay: '0.2s' }}>
              <h3 className="mb-3">What is your pricing structure?</h3>
              <p>
                We offer both fixed-price and time-and-materials pricing models. For well-defined projects, 
                we typically use a fixed-price approach, while for more complex or evolving projects, 
                a time-and-materials model might be more appropriate. We'll recommend the best pricing 
                structure for your specific project during our consultation.
              </p>
            </div>
            
            <div className="card animate-fadeUp" style={{ padding: '1.5rem', animationDelay: '0.3s' }}>
              <h3 className="mb-3">Do you provide maintenance and support?</h3>
              <p>
                Yes, we offer ongoing maintenance and support services to ensure your digital products 
                continue to function optimally. We provide different support packages based on your needs, 
                from basic technical support to comprehensive maintenance including updates, security patches, 
                and performance optimization.
              </p>
            </div>
            
            <div className="card animate-fadeUp" style={{ padding: '1.5rem', animationDelay: '0.4s' }}>
              <h3 className="mb-3">Can you work with an existing design?</h3>
              <p>
                Absolutely! We're happy to work with existing designs or collaborate with your design team. 
                We can implement designs created in tools like Figma, Adobe XD, or Sketch, ensuring the final 
                product matches the design specifications exactly.
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="cta-section">
        <div className="container cta-content">
          <h2 className="cta-title animate-fadeUp">Ready to Start Your Project?</h2>
          <p className="cta-description animate-fadeUp delay-1">
            Contact us today for a free consultation and let's discuss how we can help you achieve your business goals.
          </p>
          <Link to="/contact" className="btn btn-secondary animate-fadeUp delay-2">
            Get a Free Consultation
          </Link>
        </div>
      </section>
    </>
  );
};

export default Services;